import { MediaPlayer, PlayerEvent } from '@mi-front/core';
import debugFn from 'debug';
import EventEmitter from 'events';
import Player from 'video.js/dist/types/player';

/**
 * Adapter that wraps a video.js player to implement the MediaPlayer interface,
 * enabling its use with the playback system.
 */
export class VideoJsMediaPlayer extends EventEmitter implements MediaPlayer {
  private debug: debugFn.Debugger;

  constructor(
    private videoJsPlayer: Player,
    private config: { baseTs: number; playerId?: string },
  ) {
    super();

    this.debug = debugFn(`VideoMediaPlayer-${config.playerId}`);

    this.debug(
      'initialize videojs adapter with baseTs=%s',
      this.config.baseTs ? new Date(this.config.baseTs).toISOString() : null,
    );

    this.videoJsPlayer.on('timeupdate', () => {
      const ts = Math.round(this.config.baseTs + (this.videoJsPlayer.currentTime() ?? 0) * 1000);
      this.emit(PlayerEvent.TS_UPDATE, ts);
    });

    this.videoJsPlayer.on('error', (error: Error) => {
      this.debug('error: %s', error.message);
      this.emit(PlayerEvent.ERROR, error);
    });

    this.videoJsPlayer.on('seeking', () => {
      this.emit(PlayerEvent.SEEKING_START);
    });

    this.videoJsPlayer.on('seeked', () => {
      this.emit(PlayerEvent.SEEKING_END);
    });

    this.videoJsPlayer.on('playing', () => {
      this.emit(PlayerEvent.PLAYING);
    });

    this.videoJsPlayer.on('pause', () => {
      this.emit(PlayerEvent.PAUSED);
    });
  }

  play() {
    if (!this.videoJsPlayer.paused()) {
      return;
    }

    this.debug('play');
    this.videoJsPlayer.play()?.catch((error) => {
      console.log(error);
    });
  }

  pause(): void {
    if (this.videoJsPlayer.paused()) {
      return;
    }

    this.debug('pause');
    this.videoJsPlayer.pause();
  }

  seek(args: { time: number; playAfterSeek?: boolean }): void {
    this.debug(
      'seek to %d (play after = %s)',
      new Date().toISOString(),
      args.playAfterSeek ? 'yes' : 'no',
    );
    this.videoJsPlayer.currentTime(args.time);

    if (args.playAfterSeek) {
      this.play();
    }
  }

  mute(): void {
    this.debug('mute');
    this.videoJsPlayer.muted(true);
  }

  unmute(): void {
    this.debug('unmute');
    this.videoJsPlayer.muted(false);
  }

  isMuted(): boolean {
    return this.videoJsPlayer.muted() ?? false;
  }

  dispose(): void {
    this.debug('dispose');

    if (this.videoJsPlayer.isDisposed()) {
      return;
    }

    this.videoJsPlayer.dispose();
  }
}
